import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Search, X, Bookmark, Columns2, LayoutPanelLeft, ChevronDown, Check, Hash, Image, Send, Inbox, User } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Toggle } from '@/components/ui/toggle';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import DatePickerField from '../../../../components/DatePickerField';
import { cn } from '@/lib/utils';

import {
    setSearchQuery,
    setAssignee,
    setAuthor,
    setBookmarkedOnly,
    toggleStatusFilter,
    setViewMode,
    setPageSize,
    toggleChannelId,
    toggleGroupChannels,
    clearChannelIds,
    setMediaTypeFilter,
    setSentBy,
    setSentTo,
    setDateRangeFilter,
    performSearch
} from '../../../../redux/actions/search.action';
import {
    selectQuery,
    selectAssignee,
    selectAuthor,
    selectBookmarkedOnly,
    selectStatusFilter,
    selectChannelIds,
    selectChannelsGrouped,
    selectMediaTypeFilter,
    selectSentBy,
    selectSentTo,
    selectDateFrom,
    selectDateTo,
    selectUsers,
    selectViewMode,
    selectPageSize,
    selectIsFieldEdited
} from '../../../../redux/selectors/search.selectors';

const STATUS_OPTIONS = [
    "Taking Longer",
    "Doing",
    "Doing-in-bg",
    "Paused",
    "TODO",
    "Block-on-asm",
    "Blocked-on-purchase",
    "Blocked-on-discussion",
    "Blocked",
    "Done"
];

const MEDIA_TYPE_OPTIONS = [
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' },
    { value: 'audio', label: 'Audio' },
    { value: 'other', label: 'Other' }
];

const PAGE_SIZE_OPTIONS = [10, 15, 20, 25, 50, 100];

// Component for rendering a group row with checkbox (checkbox on right)
const ChannelGroupRow = ({ group, selectedSet, onToggleGroup, onToggleChannel, filterText }) => {
    const groupChannelIds = useMemo(() => group.channels.map((c) => c.id), [group.channels]);
    const selectedCount = useMemo(
        () => groupChannelIds.reduce((acc, id) => acc + (selectedSet.has(id) ? 1 : 0), 0),
        [groupChannelIds, selectedSet]
    );

    const allSelected = groupChannelIds.length > 0 && selectedCount === groupChannelIds.length;

    const visibleChannels = useMemo(() => {
        const q = (filterText || '').trim().toLowerCase();
        if (!q) return group.channels;
        return group.channels.filter((c) => (c.name || '').toLowerCase().includes(q));
    }, [group.channels, filterText]);

    if (visibleChannels.length === 0 && filterText) return null;

    return (
        <div className="mb-2">
            {/* Group header with checkbox on right */}
            <label className="flex items-center gap-1.5 py-1 cursor-pointer hover:bg-muted rounded px-1">
                <span className="text-sm font-medium flex-1">{group.groupName}</span>
                <span className="text-xs text-muted-foreground mr-2">
                    {selectedCount}/{groupChannelIds.length}
                </span>
                <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => onToggleGroup(group.groupId, groupChannelIds)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
            </label>

            {/* Channels always visible (no collapse) with checkbox on right */}
            <div className="ml-4 space-y-0.5">
                {visibleChannels.map((ch) => (
                    <label
                        key={ch.id}
                        className="flex items-center gap-2 py-0.5 px-1 hover:bg-muted rounded cursor-pointer"
                    >
                        <Hash className="h-3 w-3 text-violet-500" />
                        <span className="text-xs truncate flex-1">{ch.name}</span>
                        <input
                            type="checkbox"
                            checked={selectedSet.has(ch.id)}
                            onChange={() => onToggleChannel(ch.id)}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                    </label>
                ))}
                {visibleChannels.length === 0 && (
                    <div className="text-xs text-muted-foreground py-1 px-1">No channels match.</div>
                )}
            </div>
        </div>
    );
};

const SearchFilters = ({ onClearFilters }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const searchInputRef = useRef(null);
    const [assigneeOpen, setAssigneeOpen] = useState(false);
    const [authorOpen, setAuthorOpen] = useState(false);
    const [channelOpen, setChannelOpen] = useState(false);
    const [channelFilterText, setChannelFilterText] = useState('');
    const [sentByOpen, setSentByOpen] = useState(false);
    const [sentToOpen, setSentToOpen] = useState(false);

    // Selectors
    const query = useSelector(selectQuery);
    const assignee = useSelector(selectAssignee);
    const author = useSelector(selectAuthor);
    const bookmarkedOnly = useSelector(selectBookmarkedOnly);
    const status = useSelector(selectStatusFilter);
    const channelIds = useSelector(selectChannelIds);
    const { personalChannel, groups } = useSelector(selectChannelsGrouped);
    const mediaType = useSelector(selectMediaTypeFilter);
    const sentBy = useSelector(selectSentBy);
    const sentTo = useSelector(selectSentTo);
    const dateFrom = useSelector(selectDateFrom);
    const dateTo = useSelector(selectDateTo);
    const users = useSelector(selectUsers);
    const viewMode = useSelector(selectViewMode);
    const pageSize = useSelector(selectPageSize);

    // Derived state
    const selectedChannelSet = useMemo(() => new Set(channelIds), [channelIds]);
    const selectedChannelCount = channelIds.length;

    // Field edited checks
    const isQueryEdited = useSelector(state => selectIsFieldEdited(state, 'query'));
    const isAssigneeEdited = useSelector(state => selectIsFieldEdited(state, 'assignee'));
    const isAuthorEdited = useSelector(state => selectIsFieldEdited(state, 'author'));
    const isBookmarkedEdited = useSelector(state => selectIsFieldEdited(state, 'bookmarkedOnly'));
    const isStatusEdited = useSelector(state => selectIsFieldEdited(state, 'status'));

    const selectedAssignee = users.find(u => u.id === assignee);
    const selectedAuthor = users.find(u => u.id === author);
    const selectedSentByUser = users.find(u => u.id === sentBy);
    const selectedSentToUser = users.find(u => u.id === sentTo);

    // Channel filter handlers
    const handleToggleChannel = (id) => dispatch(toggleChannelId(id));
    const handleToggleGroup = (groupId, ids) => dispatch(toggleGroupChannels(groupId, ids));
    const handleClearChannels = () => dispatch(clearChannelIds());

    const getInitials = (firstName, lastName) => {
        return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            dispatch(performSearch());
        }
    };

    const handleClearQuery = () => {
        dispatch(setSearchQuery(''));
    };

    // Auto-focus search input when navigating to /search
    useLayoutEffect(() => {
        if (location.pathname !== "/search") return;

        let cancelled = false;
        let tries = 0;

        const isEditableElement = (el) => {
            if (!el) return false;
            const tag = (el.tagName || "").toLowerCase();
            return tag === "input" || tag === "textarea" || el.isContentEditable === true;
        };

        const tryFocus = () => {
            if (cancelled) return;

            const el = searchInputRef.current;
            if (!el) {
                if (tries++ < 20) requestAnimationFrame(tryFocus);
                return;
            }

            // Don't steal focus if user is already typing somewhere else
            const active = document.activeElement;
            if (active && active !== el && isEditableElement(active)) return;

            const r = el.getBoundingClientRect();
            const visible = r.width > 0 && r.height > 0 && !el.disabled;

            if (!visible) {
                if (tries++ < 20) requestAnimationFrame(tryFocus);
                return;
            }

            el.focus({ preventScroll: true });

            // If focus didn't stick yet, retry
            if (document.activeElement !== el) {
                if (tries++ < 20) requestAnimationFrame(tryFocus);
                return;
            }

            // Place caret at end
            if (typeof el.setSelectionRange === "function") {
                const len = el.value?.length ?? 0;
                el.setSelectionRange(len, len);
            }
        };

        requestAnimationFrame(tryFocus);

        return () => {
            cancelled = true;
        };
    }, [location.pathname]);

    return (
        <div className="flex items-center gap-4 px-4 py-4">
            {/* LEFT SECTION: Bookmark + Search (vertically centered) */}
            <div className="flex items-center gap-2 w-[38%] min-w-[300px] shrink-0">
                {/* Bookmark Toggle */}
                <Toggle
                    pressed={bookmarkedOnly}
                    onPressedChange={(pressed) => dispatch(setBookmarkedOnly(pressed))}
                    size="sm"
                    title="Bookmark filter • Affects: Folders, Links"
                    className={cn(
                        "h-8 w-8 p-0 shrink-0 data-[state=on]:bg-amber-100 data-[state=on]:text-amber-600",
                        isBookmarkedEdited && "ring-2 ring-yellow-400"
                    )}
                >
                    <Bookmark className={cn("h-4 w-4", bookmarkedOnly && "fill-current")} />
                </Toggle>

                {/* Search Input */}
                <div className="flex items-center gap-1 flex-1" title="Text search • Affects: Folders (name), Links (name/URL), Messages (text)">
                    <div className="relative flex-1">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                            ref={searchInputRef}
                            placeholder="Search text..."
                            value={query}
                            onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                            onKeyDown={handleSearchKeyDown}
                            className={cn(
                                "h-8 pl-7 pr-8 text-sm",
                                isQueryEdited && "ring-2 ring-yellow-400"
                            )}
                        />
                        {query && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 top-0 h-8 w-8 hover:bg-transparent"
                                onClick={handleClearQuery}
                            >
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                    <Button
                        size="sm"
                        className="h-8 px-3 shrink-0"
                        disabled={!isQueryEdited}
                        onClick={() => dispatch(performSearch())}
                    >
                        <Search className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>

            {/* MIDDLE SECTION: Two stacked rows of filters */}
            <div className="flex flex-col gap-2 flex-1">
                {/* Row 1: Assignee, Author, Status, Media */}
                <div className="flex items-center gap-2">
                    {/* Assignee Combobox */}
                    <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                role="combobox"
                                aria-expanded={assigneeOpen}
                                title="Assignee filter • Affects: Folders, Links"
                                className={cn(
                                    "h-8 gap-1.5 px-2 min-w-[160px] justify-start",
                                    isAssigneeEdited && "ring-2 ring-yellow-400"
                                )}
                            >
                                {selectedAssignee ? (
                                    <>
                                        <Avatar className="h-5 w-5">
                                            <AvatarImage src={selectedAssignee.picture} />
                                            <AvatarFallback className="text-[10px]">
                                                {getInitials(selectedAssignee.first_name, selectedAssignee.last_name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="truncate text-xs">
                                            {selectedAssignee.first_name}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-muted-foreground text-xs">Assignee</span>
                                )}
                                <ChevronDown className="h-3 w-3 ml-auto opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[220px] p-0" align="start">
                            <Command filter={(value, search) => {
                                if (!search) return 1;
                                const searchLower = search.toLowerCase();
                                const valueLower = value.toLowerCase();
                                if (valueLower.includes(searchLower)) return 1;
                                return 0;
                            }}>
                                <CommandInput placeholder="Search user..." className="h-9" />
                                <CommandList>
                                    <CommandEmpty>No user found.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem
                                            value="clear selection"
                                            onSelect={() => {
                                                dispatch(setAssignee(null));
                                                setAssigneeOpen(false);
                                            }}
                                            className="text-muted-foreground"
                                        >
                                            Clear selection
                                        </CommandItem>
                                        {users.length > 0 ? users.map((user) => {
                                            const fullName = `${user.first_name} ${user.last_name}`;
                                            const searchText = `${fullName} ${user.email || ''}`.toLowerCase();
                                            return (
                                                <CommandItem
                                                    key={user.id}
                                                    value={searchText}
                                                    onSelect={() => {
                                                        dispatch(setAssignee(user.id));
                                                        setAssigneeOpen(false);
                                                    }}
                                                >
                                                    <Avatar className="h-5 w-5">
                                                        <AvatarImage src={user.picture} />
                                                        <AvatarFallback className="text-[10px]">
                                                            {getInitials(user.first_name, user.last_name)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <span className="truncate text-xs">
                                                        {fullName}
                                                    </span>
                                                    <Check
                                                        className={cn(
                                                            "ml-auto h-4 w-4",
                                                            assignee === user.id ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                </CommandItem>
                                            );
                                        }) : (
                                            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                                                {users.length === 0 ? 'No users available' : 'Loading users...'}
                                            </div>
                                        )}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>

                    {/* Author Combobox */}
                    <Popover open={authorOpen} onOpenChange={setAuthorOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                role="combobox"
                                aria-expanded={authorOpen}
                                title="Author/Creator filter • Affects: Folders, Links, Messages"
                                className={cn(
                                    "h-8 gap-1.5 px-2 min-w-[160px] justify-start",
                                    isAuthorEdited && "ring-2 ring-yellow-400"
                                )}
                            >
                                {selectedAuthor ? (
                                    <>
                                        <Avatar className="h-5 w-5">
                                            <AvatarImage src={selectedAuthor.picture} />
                                            <AvatarFallback className="text-[10px]">
                                                {getInitials(selectedAuthor.first_name, selectedAuthor.last_name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="truncate text-xs">
                                            {selectedAuthor.first_name}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-muted-foreground text-xs">Author</span>
                                )}
                                <ChevronDown className="h-3 w-3 ml-auto opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[220px] p-0" align="start">
                            <Command filter={(value, search) => {
                                if (!search) return 1;
                                const searchLower = search.toLowerCase();
                                const valueLower = value.toLowerCase();
                                if (valueLower.includes(searchLower)) return 1;
                                return 0;
                            }}>
                                <CommandInput placeholder="Search user..." className="h-9" />
                                <CommandList>
                                    <CommandEmpty>No user found.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem
                                            value="clear selection"
                                            onSelect={() => {
                                                dispatch(setAuthor(null));
                                                setAuthorOpen(false);
                                            }}
                                            className="text-muted-foreground"
                                        >
                                            Clear selection
                                        </CommandItem>
                                        {users.length > 0 ? users.map((user) => {
                                            const fullName = `${user.first_name} ${user.last_name}`;
                                            const searchText = `${fullName} ${user.email || ''}`.toLowerCase();
                                            return (
                                                <CommandItem
                                                    key={user.id}
                                                    value={searchText}
                                                    onSelect={() => {
                                                        dispatch(setAuthor(user.id));
                                                        setAuthorOpen(false);
                                                    }}
                                                >
                                                    <Avatar className="h-5 w-5">
                                                        <AvatarImage src={user.picture} />
                                                        <AvatarFallback className="text-[10px]">
                                                            {getInitials(user.first_name, user.last_name)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <span className="truncate text-xs">
                                                        {fullName}
                                                    </span>
                                                    <Check
                                                        className={cn(
                                                            "ml-auto h-4 w-4",
                                                            author === user.id ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                </CommandItem>
                                            );
                                        }) : (
                                            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                                                {users.length === 0 ? 'No users available' : 'Loading users...'}
                                            </div>
                                        )}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>

                    {/* Status Multi-Select Popover */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                title="Status filter • Affects: Folders only"
                                className={cn(
                                    "h-8 gap-1.5 px-2 min-w-[145px] justify-start",
                                    isStatusEdited && "ring-2 ring-yellow-400"
                                )}
                            >
                                {status.length > 0 ? (
                                    <div className="flex items-center gap-1">
                                        <Badge variant="secondary" className="h-5 px-1 text-[10px]">
                                            {status.length}
                                        </Badge>
                                        <span className="text-xs truncate">Status</span>
                                    </div>
                                ) : (
                                    <span className="text-muted-foreground text-xs">Status</span>
                                )}
                                <ChevronDown className="h-3 w-3 ml-auto opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[180px] p-0" align="start">
                            <ScrollArea className="h-[250px]">
                                <div className="p-1">
                                    {STATUS_OPTIONS.map((statusItem) => (
                                        <Button
                                            key={statusItem}
                                            variant="ghost"
                                            size="sm"
                                            className="w-full justify-start gap-2 h-7 text-xs"
                                            onClick={() => dispatch(toggleStatusFilter(statusItem))}
                                        >
                                            <div className={cn(
                                                "h-4 w-4 border rounded flex items-center justify-center",
                                                status.includes(statusItem) && "bg-primary border-primary"
                                            )}>
                                                {status.includes(statusItem) && (
                                                    <Check className="h-3 w-3 text-primary-foreground" />
                                                )}
                                            </div>
                                            <span className="truncate">{statusItem}</span>
                                        </Button>
                                    ))}
                                </div>
                            </ScrollArea>
                        </PopoverContent>
                    </Popover>

                    {/* Media Type Select - for message search */}
                    <Select
                        value={mediaType || ''}
                        onValueChange={(val) => dispatch(setMediaTypeFilter(val === 'all' ? null : val))}
                    >
                        <SelectTrigger className="h-8 w-[145px] text-xs" title="Media type filter • Affects: Messages only">
                            <div className="flex items-center gap-1.5">
                                <Image className="h-3 w-3 text-muted-foreground" />
                                <SelectValue placeholder="Media" />
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectLabel className="text-xs">Media Type</SelectLabel>
                                <SelectItem value="all" className="text-xs">All</SelectItem>
                                {MEDIA_TYPE_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                        {opt.label}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                {/* Row 2: Channels, Sent by, Sent to, From, To */}
                <div className="flex items-center gap-2">
                    {/* Channel Multi-Select Popover - for message search */}
                    <Popover open={channelOpen} onOpenChange={(open) => {
                        setChannelOpen(open);
                        if (!open) setChannelFilterText('');
                    }}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                role="combobox"
                                aria-expanded={channelOpen}
                                title="Channel filter • Affects: Messages only"
                                className="h-8 gap-1.5 px-2 min-w-[160px] justify-start"
                            >
                                {selectedChannelCount > 0 ? (
                                    <>
                                        <Hash className="h-3 w-3 text-violet-500" />
                                        <Badge variant="secondary" className="h-5 px-1 text-[10px]">
                                            {selectedChannelCount}
                                        </Badge>
                                        <span className="text-xs">Channels</span>
                                    </>
                                ) : (
                                    <span className="text-muted-foreground text-xs">Channels</span>
                                )}
                                <ChevronDown className="h-3 w-3 ml-auto opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[280px] p-0" align="start">
                            <div className="p-2 border-b">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium">Select Channels</span>
                                    {selectedChannelCount > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 px-2 text-xs"
                                            onClick={handleClearChannels}
                                        >
                                            Clear all
                                        </Button>
                                    )}
                                </div>
                                <Input
                                    placeholder="Search channels..."
                                    value={channelFilterText}
                                    onChange={(e) => setChannelFilterText(e.target.value)}
                                    className="h-8 text-xs"
                                />
                            </div>
                            <ScrollArea className="h-[300px]">
                                <div className="p-2">
                                    {/* Personal Channel (at top, not part of any group) */}
                                    {personalChannel && (
                                        <div className="mb-3 pb-2 border-b">
                                            <label className="flex items-center gap-2 py-1 px-1 hover:bg-muted rounded cursor-pointer">
                                                <User className="h-3.5 w-3.5 text-blue-500" />
                                                <span className="text-xs truncate flex-1">{personalChannel.name}</span>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedChannelSet.has(personalChannel.id)}
                                                    onChange={() => handleToggleChannel(personalChannel.id)}
                                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                            </label>
                                        </div>
                                    )}

                                    {/* Groups and Others */}
                                    {groups.length > 0 && (
                                        <div className="space-y-1">
                                            {groups.map((g) => (
                                                <ChannelGroupRow
                                                    key={String(g.groupId)}
                                                    group={g}
                                                    selectedSet={selectedChannelSet}
                                                    onToggleGroup={handleToggleGroup}
                                                    onToggleChannel={handleToggleChannel}
                                                    filterText={channelFilterText}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {groups.length === 0 && !personalChannel && (
                                        <div className="text-xs text-muted-foreground text-center py-4">
                                            No channels available
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                            {selectedChannelCount > 0 && (
                                <div className="p-2 border-t flex items-center justify-between">
                                    <span className="text-xs text-muted-foreground">
                                        Selected: <strong>{selectedChannelCount}</strong>
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2 text-xs"
                                        onClick={handleClearChannels}
                                    >
                                        <X className="h-3 w-3 mr-1" />
                                        Clear
                                    </Button>
                                </div>
                            )}
                        </PopoverContent>
                    </Popover>

                    {/* Sent By Combobox - for message search */}
                    <Popover open={sentByOpen} onOpenChange={setSentByOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                role="combobox"
                                aria-expanded={sentByOpen}
                                title="Sent by filter • Affects: Messages only"
                                className="h-8 gap-1.5 px-2 min-w-[130px] justify-start"
                            >
                                {selectedSentByUser ? (
                                    <>
                                        <Avatar className="h-5 w-5">
                                            <AvatarImage src={selectedSentByUser.picture} />
                                            <AvatarFallback className="text-[10px]">
                                                {getInitials(selectedSentByUser.first_name, selectedSentByUser.last_name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="truncate text-xs">
                                            {selectedSentByUser.first_name}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-3 w-3 text-muted-foreground" />
                                        <span className="text-muted-foreground text-xs">Sent by</span>
                                    </>
                                )}
                                <ChevronDown className="h-3 w-3 ml-auto opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[220px] p-0" align="start">
                            <Command filter={(value, search) => {
                                if (!search) return 1;
                                const searchLower = search.toLowerCase();
                                const valueLower = value.toLowerCase();
                                if (valueLower.includes(searchLower)) return 1;
                                return 0;
                            }}>
                                <CommandInput placeholder="Search user..." className="h-9" />
                                <CommandList>
                                    <CommandEmpty>No user found.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem
                                            value="clear selection"
                                            onSelect={() => {
                                                dispatch(setSentBy(null));
                                                setSentByOpen(false);
                                            }}
                                            className="text-muted-foreground"
                                        >
                                            Clear selection
                                        </CommandItem>
                                        {users.length > 0 ? users.map((user) => {
                                            const fullName = `${user.first_name} ${user.last_name}`;
                                            const searchText = `${fullName} ${user.email || ''}`.toLowerCase();
                                            return (
                                                <CommandItem
                                                    key={user.id}
                                                    value={searchText}
                                                    onSelect={() => {
                                                        dispatch(setSentBy(user.id));
                                                        setSentByOpen(false);
                                                    }}
                                                >
                                                    <Avatar className="h-5 w-5">
                                                        <AvatarImage src={user.picture} />
                                                        <AvatarFallback className="text-[10px]">
                                                            {getInitials(user.first_name, user.last_name)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <span className="truncate text-xs">
                                                        {fullName}
                                                    </span>
                                                    <Check
                                                        className={cn(
                                                            "ml-auto h-4 w-4",
                                                            sentBy === user.id ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                </CommandItem>
                                            );
                                        }) : (
                                            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                                                {users.length === 0 ? 'No users available' : 'Loading users...'}
                                            </div>
                                        )}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>

                    {/* Sent To Combobox - for message search (personal channel + @mentions) */}
                    <Popover open={sentToOpen} onOpenChange={setSentToOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                role="combobox"
                                aria-expanded={sentToOpen}
                                title="Sent to filter • Affects: Messages only (personal channel + @mentions)"
                                className="h-8 gap-1.5 px-2 min-w-[130px] justify-start"
                            >
                                {selectedSentToUser ? (
                                    <>
                                        <Avatar className="h-5 w-5">
                                            <AvatarImage src={selectedSentToUser.picture} />
                                            <AvatarFallback className="text-[10px]">
                                                {getInitials(selectedSentToUser.first_name, selectedSentToUser.last_name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="truncate text-xs">
                                            {selectedSentToUser.first_name}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <Inbox className="h-3 w-3 text-muted-foreground" />
                                        <span className="text-muted-foreground text-xs">Sent to</span>
                                    </>
                                )}
                                <ChevronDown className="h-3 w-3 ml-auto opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[220px] p-0" align="start">
                            <Command filter={(value, search) => {
                                if (!search) return 1;
                                const searchLower = search.toLowerCase();
                                const valueLower = value.toLowerCase();
                                if (valueLower.includes(searchLower)) return 1;
                                return 0;
                            }}>
                                <CommandInput placeholder="Search user..." className="h-9" />
                                <CommandList>
                                    <CommandEmpty>No user found.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem
                                            value="clear selection"
                                            onSelect={() => {
                                                dispatch(setSentTo(null));
                                                setSentToOpen(false);
                                            }}
                                            className="text-muted-foreground"
                                        >
                                            Clear selection
                                        </CommandItem>
                                        {users.length > 0 ? users.map((user) => {
                                            const fullName = `${user.first_name} ${user.last_name}`;
                                            const searchText = `${fullName} ${user.email || ''}`.toLowerCase();
                                            return (
                                                <CommandItem
                                                    key={user.id}
                                                    value={searchText}
                                                    onSelect={() => {
                                                        dispatch(setSentTo(user.id));
                                                        setSentToOpen(false);
                                                    }}
                                                >
                                                    <Avatar className="h-5 w-5">
                                                        <AvatarImage src={user.picture} />
                                                        <AvatarFallback className="text-[10px]">
                                                            {getInitials(user.first_name, user.last_name)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <span className="truncate text-xs">
                                                        {fullName}
                                                    </span>
                                                    <Check
                                                        className={cn(
                                                            "ml-auto h-4 w-4",
                                                            sentTo === user.id ? "opacity-100" : "opacity-0"
                                                        )}
                                                    />
                                                </CommandItem>
                                            );
                                        }) : (
                                            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                                                {users.length === 0 ? 'No users available' : 'Loading users...'}
                                            </div>
                                        )}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>

                    {/* Date range pickers - for folders, links and message search */}
                    <DatePickerField
                        label="From"
                        title="From date filter • Affects: Folders, Links, Messages"
                        value={dateFrom}
                        maxDate={dateTo}
                        onChange={(value) => dispatch(setDateRangeFilter(value, dateTo))}
                    />
                    <DatePickerField
                        label="To"
                        title="To date filter • Affects: Folders, Links, Messages"
                        value={dateTo}
                        minDate={dateFrom}
                        onChange={(value) => dispatch(setDateRangeFilter(dateFrom, value))}
                    />
                </div>
            </div>

            {/* RIGHT SECTION: Controls (single row, vertically centered) */}
            <div className="flex items-center gap-2 shrink-0">
                {/* Clear Filters */}
                <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={onClearFilters}
                    title="Clear all filters"
                >
                    <X className="h-4 w-4" />
                </Button>

                {/* Page Size */}
                <Select
                    value={pageSize.toString()}
                    onValueChange={(val) => dispatch(setPageSize(parseInt(val)))}
                >
                    <SelectTrigger className="h-8 w-[70px] text-xs">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            <SelectLabel className="text-xs">Per page</SelectLabel>
                            {PAGE_SIZE_OPTIONS.map((size) => (
                                <SelectItem key={size} value={size.toString()} className="text-xs">
                                    {size}
                                </SelectItem>
                            ))}
                        </SelectGroup>
                    </SelectContent>
                </Select>

                {/* View Mode Toggle */}
                <ToggleGroup
                    type="single"
                    value={viewMode}
                    onValueChange={(val) => val && dispatch(setViewMode(val))}
                    className="h-8"
                >
                    <ToggleGroupItem value="tabs" className="h-8 w-8 p-0" title="Tabs view">
                        <LayoutPanelLeft className="h-4 w-4" />
                    </ToggleGroupItem>
                    <ToggleGroupItem value="split" className="h-8 w-8 p-0" title="Split view">
                        <Columns2 className="h-4 w-4" />
                    </ToggleGroupItem>
                </ToggleGroup>
            </div>
        </div>
    );
};

export default SearchFilters;
